/**
 * The pilot study, in the app.
 *  Teacher: make a class (name, study-code prefix, weekly minutes) → a join code to give families → a roster with
 *           minutes this week against the target and the before/after check-ups → a spreadsheet with study codes,
 *           never names.
 *  Parent:  on the child's page, "Join a class" (type the code once) → "This week: 42 of 60 minutes".
 */
import type { ActivityRow, ClassInfo, ClassMember, Membership, RosterRow, Store } from "../data/store";
import type { StudentProgress } from "../engine/progress";
import { summary } from "../engine/progress";
import { csv, minutesByWeek, minutesOf, thisWeek, weekStartISO, weeksSince } from "../engine/study";
import { LEVELS, anyLesson, levelOf, lessonName } from "../lessons/levels";
import type { CheckItem, Lesson } from "../lessons/types";
import { MISCONCEPTIONS } from "../lessons/misconceptions";
import { checkScore } from "./lessonView";
import { upNextLesson } from "./home";
import { esc } from "./keypad";
import { factsSummary, formItems } from "../lessons/factsTest";

const itemsOf = (l?: Lesson) => (l ? (l.steps[0] as { items: CheckItem[] }).items : []);
/** The level whose check-ups count for this child: where the diagnostic or teacher placed them (Level 3 if not placed). */
const studyLevel = (p: StudentProgress) => levelOf(p.placement?.lesson ?? p.diagnostic?.placed ?? "") ?? LEVELS.find((v) => v.id === "3")!;
function checks(p: StudentProgress) {
  const v = studyLevel(p);
  const one = (l?: Lesson) => { const r = l ? p.checks?.[l.steps[0]!.id] : undefined; return r?.date ? { ...checkScore(itemsOf(l), r.results), date: r.date } : null; };
  return { level: v, pre: one(v.pre), post: one(v.post) };
}
const pct = (s: { right: number; total: number } | null) => (s && s.total ? Math.round((100 * s.right) / s.total) : null);
const mins = (n: number) => Math.round(n);

/** One child in a class: progress plus time on task, added up (by the database, or here from the answers). */
export interface Row { m: ClassMember; p: StudentProgress; byWk: Map<string, number>; total: number; answers: number; firstTry: number; top: string; fluent: number; correct: number }
export function rowFromRoster(r: RosterRow): Row {
  const byWk = new Map(Object.entries(r.weeks).sort(([a], [b]) => a.localeCompare(b)));
  return { m: r.member, p: r.progress, byWk, total: [...byWk.values()].reduce((x, y) => x + y, 0), answers: r.answers, firstTry: r.firstTry,
    top: r.top.map((t) => `${t.code} (${t.n})`).join("; "), fluent: r.fluent, correct: r.correct };
}
export function rowFromAnswers(m: ClassMember, p: StudentProgress, act: ActivityRow[]): Row {
  const counts = new Map<string, number>(); for (const r of act) if (r.misconception && r.misconception !== "skipped") counts.set(r.misconception, (counts.get(r.misconception) ?? 0) + 1);
  const sm = summary(p);
  return { m, p, byWk: minutesByWeek(act), total: minutesOf(act), answers: act.length, firstTry: act.filter((r) => r.firstTry).length,
    top: [...counts.entries()].sort((x, y) => y[1] - x[1]).slice(0, 3).map(([c, n]) => `${c} (${n})`).join("; "), fluent: sm.fluent, correct: sm.correct };
}
async function loadRows(store: Store, classId: string): Promise<Row[]> {
  const fast = await store.classRoster(classId);
  if (fast) return fast.map(rowFromRoster);
  const members = await store.listMembers(classId);
  const ps = await store.loadProgressMany(members.map((m) => m.studentId));
  return Promise.all(members.map(async (m) => rowFromAnswers(m, ps.get(m.studentId)!, await store.activity(m.studentId, m.joinedAt))));
}

/* ---------------- Teacher ---------------- */
export async function mountStudy(box: HTMLElement, store: Store): Promise<void> {
  if (store.kind !== "supabase") { box.innerHTML = ""; return; }
  let classes: ClassInfo[] = [];
  try { classes = await store.listClasses(); } catch (e) { console.warn(e); }
  box.innerHTML = `
    <h1 style="margin-bottom:6px">Study classes</h1>
    <p class="muted" style="margin-top:0">Families join a class with its code. You see their minutes and check-ups; the spreadsheet uses study codes, never names.</p>
    <div id="classes"></div>
    <form class="card" id="newClass" style="margin-top:14px">
      <h3 style="margin-bottom:6px">New class</h3>
      <div class="row" style="margin:0;flex-wrap:wrap;gap:10px">
        <label>Name <input id="cn" maxlength="60" required placeholder="Valley Academy — 3rd grade"></label>
        <label>Study-code letters <input id="cp" maxlength="4" required placeholder="VA" style="width:5em;text-transform:uppercase"></label>
        <label>Minutes a week <input id="cw" type="number" min="0" max="600" value="60" style="width:5em"></label>
        <button class="btn small" type="submit">Make the class</button><span class="note" id="cnote" style="margin:0"></span>
      </div>
    </form>`;
  const list = box.querySelector<HTMLElement>("#classes")!;
  if (!classes.length) list.innerHTML = '<p class="muted">No study classes yet.</p>';
  for (const c of classes) {
    const card = document.createElement("div");
    card.className = "card"; card.style.marginTop = "12px";
    list.appendChild(card);
    await renderClass(card, c, store);
  }
  const f = box.querySelector<HTMLFormElement>("#newClass")!;
  f.onsubmit = async (e) => {
    e.preventDefault();
    const name = f.querySelector<HTMLInputElement>("#cn")!.value.trim(), prefix = f.querySelector<HTMLInputElement>("#cp")!.value.trim().toUpperCase().replace(/[^A-Z0-9]/g, ""), w = f.querySelector<HTMLInputElement>("#cw")!.value;
    if (!name || !prefix) return;
    try { await store.createClass(name, prefix, w === "" ? null : Number(w)); mountStudy(box, store); }
    catch (err) { f.querySelector("#cnote")!.textContent = (err as Error).message; }
  };
}

async function renderClass(card: HTMLElement, c: ClassInfo, store: Store) {
  card.innerHTML = `<h3 style="margin:0">${esc(c.name)}</h3><p class="muted">Loading…</p>`;
  const rows = await loadRows(store, c.id);
  const wk = thisWeek(), target = c.weeklyMinutes;
  card.innerHTML = `
    <div class="row" style="justify-content:space-between;align-items:flex-start;margin:0;flex-wrap:wrap">
      <div><h3 style="margin:0 0 4px">${esc(c.name)}</h3>
        <p style="margin:0">Class code for families: <b style="font-size:1.4rem;letter-spacing:.12em">${esc(c.joinCode)}</b></p>
        <p class="note" style="margin:4px 0 0">Parents sign in, open their child, tap <b>Join a class</b>, and type the code. Study codes start with ${esc(c.prefix || "S")}-.</p></div>
      <form class="row tgt" style="margin:0"><label>Target <input type="number" min="0" max="600" value="${target ?? ""}" style="width:5em"> minutes a week</label><button class="btn ghost small" type="submit">Save</button></form>
    </div>
    <form class="row pace" style="margin:8px 0 0;flex-wrap:wrap;gap:10px">
      <label>Level <select id="lv"><option value="">Each child's grade</option>${LEVELS.map((v) => `<option value="${v.id}"${c.level === v.id ? " selected" : ""}>${esc(v.name)}</option>`).join("")}</select></label>
      <label>The class is on <select id="pl"><option value="">No ceiling — each child at their own pace</option>${(LEVELS.find((v) => v.id === c.level)?.lessons ?? []).map((l) => `<option value="${l.id}"${c.paceLesson === l.id ? " selected" : ""}>${esc(lessonName(l))}: ${esc(l.title)}</option>`).join("")}</select></label>
      <button class="btn ghost small" type="submit">Save</button>
      <span class="note" style="margin:0">Class mode: pick the level, then move the class on one lesson at a time after each day's teaching. Nothing past that lesson opens; students who finish early review earlier lessons.</span>
    </form>
    ${rows.length ? `<div class="mapwrap" style="margin-top:10px"><table class="roster"><tr><th>Study code</th><th>Student</th><th>This week</th><th>Weeks at target</th><th>Total minutes</th><th>Beginning check-up</th><th>End check-up</th><th>Up next</th></tr>
      ${rows.map(({ m, p, byWk, total }) => {
        const now = byWk.get(wk) ?? 0, ch = checks(p), up = upNextLesson(p);
        const met = target ? [...byWk.values()].filter((x) => x >= target).length : null;
        const short = target != null && now < target;
        return `<tr><td>${esc(m.studyCode)}</td><td>${esc(m.firstName)}${m.grade != null ? `<span class="muted"> · Gr ${m.grade}</span>` : ""}</td>
          <td>${short ? `<span class="warnText">${mins(now)}</span>` : mins(now)}${target != null ? ` of ${target}` : ""}</td>
          <td>${met == null ? "—" : `${met} of ${weeksSince(m.joinedAt)}`}</td><td>${mins(total)}</td>
          <td>${ch.pre ? `${ch.pre.right}/${ch.pre.total}` : "—"}</td><td>${ch.post ? `${ch.post.right}/${ch.post.total}` : "—"}</td>
          <td>${up ? `L${up.level} ${esc(lessonName(up))}` : "done"}</td></tr>`;
      }).join("")}</table></div>
      <div class="row" style="margin-top:10px"><button class="btn small" type="button" data-csv>Download the study spreadsheet (no names)</button><button class="btn ghost small" type="button" data-facts>Download the facts-check items (no names)</button><button class="btn ghost small" type="button" data-key>What the mistake codes mean</button></div>` : '<p class="muted" style="margin-top:10px">No families have joined yet.</p>'}`;
  const pf = card.querySelector<HTMLFormElement>("form.pace")!;
  pf.querySelector<HTMLSelectElement>("#lv")!.onchange = () => {
    const lv = pf.querySelector<HTMLSelectElement>("#lv")!.value, pl = pf.querySelector<HTMLSelectElement>("#pl")!;
    pl.innerHTML = `<option value="">No ceiling — each child at their own pace</option>${(LEVELS.find((v) => v.id === lv)?.lessons ?? []).map((l) => `<option value="${l.id}">${esc(lessonName(l))}: ${esc(l.title)}</option>`).join("")}`;
  };
  pf.onsubmit = async (e) => {
    e.preventDefault();
    const lv = pf.querySelector<HTMLSelectElement>("#lv")!.value || null, pl = lv ? pf.querySelector<HTMLSelectElement>("#pl")!.value || null : null;
    await store.setClassPace(c.id, lv, pl); c.level = lv; c.paceLesson = pl; renderClass(card, c, store);
  };
  const tf = card.querySelector<HTMLFormElement>("form.tgt")!;
  tf.onsubmit = async (e) => { e.preventDefault(); const v = tf.querySelector("input")!.value; await store.setClassTarget(c.id, v === "" ? null : Number(v)); c.weeklyMinutes = v === "" ? null : Number(v); renderClass(card, c, store); };
  const b = card.querySelector<HTMLButtonElement>("[data-csv]");
  if (b) b.onclick = () => download(`${(c.prefix || "study").toLowerCase()}-study-${new Date().toISOString().slice(0, 10)}.csv`, studyCSV(rows, target));
  const fb = card.querySelector<HTMLButtonElement>("[data-facts]");
  if (fb) fb.onclick = () => download(`${(c.prefix || "study").toLowerCase()}-facts-check-${new Date().toISOString().slice(0, 10)}.csv`, factsCSV(rows));
  const k = card.querySelector<HTMLButtonElement>("[data-key]");
  if (k) k.onclick = () => download("tigard-mistake-codes.csv", mistakeKey());
}

/** One row per child, by study code. The research record: no names. */
export function studyCSV(rows: Row[], target: number | null): string {
  const head = ["study_code", "grade", "joined", "weeks_in_study", "total_minutes", "avg_minutes_per_week", "target_minutes_per_week", "weeks_at_target",
    "starting_point", "checkup_level", "beginning_checkup_right", "beginning_checkup_total", "beginning_checkup_pct", "beginning_checkup_date",
    "end_checkup_right", "end_checkup_total", "end_checkup_pct", "end_checkup_date", "gain_pct_points", "answers", "first_try_pct", "facts_fluent", "facts_correct", "up_next", "top_mistakes",
    "factscheck_pre_date", "factscheck_pre_right", "factscheck_pre_n", "factscheck_pre_fluent", "factscheck_pre_median_ms", "factscheck_pre_mul_right", "factscheck_pre_div_right",
    "factscheck_post_date", "factscheck_post_right", "factscheck_post_n", "factscheck_post_fluent", "factscheck_post_median_ms", "factscheck_post_mul_right", "factscheck_post_div_right", "factscheck_gain_right", "factscheck_gain_fluent"];
  const out: unknown[][] = [head];
  for (const { m, p, byWk, total, answers, firstTry, top, fluent, correct } of rows) {
    const weeks = weeksSince(m.joinedAt), ch = checks(p), up = upNextLesson(p);
    const placed = p.placement ? anyLesson(p.placement.lesson) : undefined;
    const a = pct(ch.pre), z = pct(ch.post);
    const fa = p.factsTest?.pre ? factsSummary(p.factsTest.pre) : undefined, fz = p.factsTest?.post ? factsSummary(p.factsTest.post) : undefined;
    const fcols = (d?: string, s?: ReturnType<typeof factsSummary>) => (s ? [d, s.all.right, s.all.n, s.all.fluent, s.all.medianMs, s.mul.right, s.div.right] : ["", "", "", "", "", "", ""]);
    out.push([m.studyCode, m.grade ?? "", m.joinedAt.slice(0, 10), weeks, Math.round(total), Math.round(total / weeks), target ?? "", target ? [...byWk.values()].filter((x) => x >= target).length : "",
      placed ? `L${placed.level} ${lessonName(placed)}` : "", ch.level.id,
      ch.pre?.right ?? "", ch.pre?.total ?? "", a ?? "", ch.pre?.date ?? "", ch.post?.right ?? "", ch.post?.total ?? "", z ?? "", ch.post?.date ?? "",
      a != null && z != null ? z - a : "", answers, answers ? Math.round((100 * firstTry) / answers) : "", fluent, correct,
      up ? `L${up.level} ${lessonName(up)}` : "done", top,
      ...fcols(p.factsTest?.pre?.date, fa), ...fcols(p.factsTest?.post?.date, fz), fa && fz ? fz.all.right - fa.all.right : "", fa && fz ? fz.all.fluent - fa.all.fluent : ""]);
  }
  return csv(out);
}
/** One row per facts-check item per child: the item-level record for the research partner. No names. */
export function factsCSV(rows: Row[]): string {
  const out: unknown[][] = [["study_code", "grade", "sitting", "form", "date", "item", "operation", "table", "other", "prompt", "answer", "result", "ms"]];
  for (const { m, p } of rows) for (const w of ["pre", "post"] as const) {
    const r = p.factsTest?.[w];
    if (!r) continue;
    for (const it of formItems(r.form)) {
      const x = r.items[it.id];
      out.push([m.studyCode, m.grade ?? "", w, r.form, r.date, it.id, it.op, it.table, it.other, it.prompt.replace(" = ?", ""), it.answer, !x || x.c === null ? "skipped" : x.c ? "right" : "wrong", x?.ms ?? ""]);
    }
  }
  return csv(out);
}
/** For the teacher's eyes: what the mistake codes in the spreadsheet mean. */
export const mistakeKey = (): string => csv([["code", "what happened", "what to say"], ...Object.entries(MISCONCEPTIONS).map(([c, m]) => [c, m.did, m.say])]);

function download(name: string, text: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

/* ---------------- Parent ---------------- */
export async function mountClassBadge(el: HTMLElement, store: Store, studentId: string, firstName: string, onJoined?: () => void): Promise<Membership | null> {
  if (store.kind !== "supabase") return null;
  const mem = await store.membership(studentId).catch(() => null);
  if (mem) {
    const now = mem.weeklyMinutes != null ? minutesOf(await store.activity(studentId, weekStartISO())) : null;
    const done = now != null && mem.weeklyMinutes != null && now >= mem.weeklyMinutes;
    el.innerHTML = `<p class="floor">${esc(mem.className)}${now != null ? ` · This week: <b>${mins(now)} of ${mem.weeklyMinutes} minutes</b>${done ? " ✓" : ""}` : ""}</p>`;
    return mem;
  }
  el.innerHTML = `<details class="joincls"><summary class="note" style="cursor:pointer">In a class? Join with the class code</summary>
    <form class="row" style="margin:8px 0 0"><input maxlength="12" aria-label="Class code" placeholder="Class code" style="width:9em;text-transform:uppercase" required><button class="btn small" type="submit">Join</button><span class="note" style="margin:0"></span></form>
    <p class="note" style="margin:6px 0 0">The teacher sees ${esc(firstName)}'s minutes and check-ups. Research results use a study code, never a name.</p></details>`;
  const f = el.querySelector("form")!;
  f.onsubmit = async (e) => {
    e.preventDefault();
    const code = f.querySelector("input")!.value.trim();
    try { await store.joinClass(studentId, code); if (onJoined) onJoined(); else void mountClassBadge(el, store, studentId, firstName); }
    catch (err) { f.querySelector("span")!.textContent = (err as Error).message; }
  };
  return null;
}

/** Pilot rule (Meliesa, Oct 29): a child in a study class takes the Beginning Check-Up for their grade's level before
 *  any lesson, the starting-point diagnostic or times-tables practice opens — so the "before" score comes before any
 *  teaching. Kindergarten has no beginning check-up, so nothing is held back there. */
export function requiredCheckUp(grade: number | null): Lesson | undefined {
  const id = grade == null ? "3" : grade <= 0 ? "K" : String(Math.min(grade, 8));
  return LEVELS.find((v) => v.id === id)?.pre;
}
